import type { KeyboardEvent, SyntheticEvent } from "react"
import { useEffect, useRef, useState } from "react"
import { IconArrowRotateAnticlockwiseOutline18 as Revert } from "nucleo-ui-outline-18"

import { IconButton, Tooltip } from "@productlane/ui"

interface PortalSlugFieldProps {
	/** Path prefix shown before the input, like `/docs/`. */
	prefix: string
	/** The live slug, or the derived preview while the slug is still unfrozen. */
	value: string
	/** False while the slug still follows the title, i.e. before first publish. */
	isFrozen: boolean
	/** What the title would produce today; enables the revert control. */
	derived?: string
	disabled?: boolean
	onSave: (next: string) => Promise<void> | void
}

// Stops the surrounding Radix menu from treating keystrokes as typeahead and
// clicks as item selection.
const stop = (event: SyntheticEvent) => event.stopPropagation()

export const PortalSlugField = ({
	prefix,
	value,
	isFrozen,
	derived,
	disabled,
	onSave,
}: PortalSlugFieldProps) => {
	const [draft, setDraft] = useState(value)
	const [isEditing, setIsEditing] = useState(false)
	// Escape blurs the input, and that blur must not save the abandoned draft.
	const cancelled = useRef(false)

	useEffect(() => {
		if (!isEditing) setDraft(value)
	}, [value, isEditing])

	const commit = async () => {
		setIsEditing(false)
		if (cancelled.current) {
			cancelled.current = false
			setDraft(value)
			return
		}
		const next = draft.trim()
		if (!next || next === value) {
			setDraft(value)
			return
		}
		await onSave(next)
	}

	const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		event.stopPropagation()
		if (event.key === "Enter") {
			event.preventDefault()
			event.currentTarget.blur()
		}
		if (event.key === "Escape") {
			event.preventDefault()
			cancelled.current = true
			setDraft(value)
			event.currentTarget.blur()
		}
	}

	const canReset = isFrozen && !!derived && derived !== value

	return (
		// The prefix and the value are siblings in one row, so the path reads as a
		// single string instead of two differently typeset halves.
		<div
			role="presentation"
			className="px-2 pb-1.5 pt-1"
			onKeyDown={stop}
			onPointerDown={stop}
			onClick={stop}
		>
			<div className="flex h-8 items-center rounded-md bg-white pl-3 pr-1 shadow-sm ring-1 ring-gray-900/15 focus-within:ring-black/30 dark:bg-transparent dark:ring-white/15 dark:focus-within:ring-white/30">
				<span className="flex-none cursor-text font-mono text-xs text-tertiary">
					{prefix}
				</span>
				<input
					name="portal-slug"
					data-testid="portal-slug-input"
					className="min-w-0 flex-1 cursor-text bg-transparent font-mono text-xs text-primary outline-none disabled:cursor-not-allowed"
					value={draft}
					disabled={disabled}
					spellCheck={false}
					autoComplete="off"
					data-1p-ignore="true"
					data-lpignore="true"
					onFocus={() => setIsEditing(true)}
					onChange={(event) => setDraft(event.target.value)}
					onBlur={() => void commit()}
					onKeyDown={onKeyDown}
				/>
				{canReset && (
					<Tooltip label="Reset to title">
						<IconButton
							Icon={Revert}
							variant="ghost"
							size="sm"
							aria-label="Reset to title"
							className="ml-1 flex-none cursor-default"
							disabled={disabled}
							onClick={() => void onSave(derived)}
						/>
					</Tooltip>
				)}
			</div>
		</div>
	)
}
